import { createSelector } from 'reselect';
import _find from 'lodash/find';
import _minBy from 'lodash/minBy';
import _keys from 'lodash/keys';

import FeatureFlags from '../../util/featureFlags';

const IMAGE_KEYS = ['pdp_front_envelope', 'pdp_back_envelope', 'pdp_rsvp_envelope', 'pdp_envelope_options', 'pdp_postcard'];

export const getIsAdminView = state => state.cards.cardCatalog.isAdminView;

// SELECTORS FOR PLP
export const getCardCatalogBusy = state => state.cards.cardCatalog.busy;
export const getSuitesCount = state => state.cards.cardCatalog.suites.count;
export const getOrderedCardSuiteUUIDs = state => state.cards.cardCatalog.suites.orderedUUIDs;
export const getCardSuitesByUUID = state => state.cards.cardCatalog.suites.byUUID;
export const getSearchFilters = state => state.cards.cardCatalog.filters;
export const getCollaborators = state => state.cards.cardCatalog.collaborators;

export const getCardSuitesAsArray = createSelector(
  getCardSuitesByUUID,
  getOrderedCardSuiteUUIDs,
  (cardSuitesByUUID, orderedCardSuiteUUIDs) => orderedCardSuiteUUIDs.map(uuid => cardSuitesByUUID[uuid])
);

export const getSuiteLeadCards = createSelector(
  getCardSuitesAsArray,
  cardSuites => cardSuites.map(cardSuite => ({ ...cardSuite.lead_card, suiteUUID: cardSuite.uuid, suiteName: cardSuite.name, collaborator: cardSuite.collaborator }))
);

export const getHasAnySearchFiltersEnabled = createSelector(
  getSearchFilters,
  searchFilters => (searchFilters.has_custom_photo || searchFilters.color !== null || searchFilters.has_foil)
);


// SELECTORS FOR PDP
export const getDetailSuite = state => state.cards.cardCatalog.detailSuite;
export const getLeadCard = state => state.cards.cardCatalog.detailSuite.leadCard;
export const getInitialVariationColor = state => state.cards.cardCatalog.initialVariationColor;
export const getThemeGroupsForCards = state => state.cards.cardCatalog.themeGroupsForCards;
export const getRelatedCardsFromLeadSuite = state => state.cards.cardCatalog.relatedCardsFromSuite;
export const getSetRelatedCardsFamilyName = state => state.cards.cardCatalog.relatedCardsFamilyName;
const getSelectedMedium = state => state.cards.cardCatalog.detailSuite.selectedMedium;
const getDigitalCard = state => state.cards.cardCatalog.detailSuite.digitalCard;

export const getDetailSuiteName = createSelector(
  getDetailSuite,
  (detailSuite) => {
    if (detailSuite && detailSuite.name) {
      return detailSuite.name;
    }
    return null;
  }
);

export const getDetailSuiteDescription = createSelector(
  getDetailSuite,
  detailSuite => detailSuite.description
);

export const getDetailSuiteLeadCard = createSelector(
  getDetailSuite,
  detailSuite => detailSuite.leadCard
);

// INV-905: return the print lead or digital lead
// this assumes DIGITAL options are a TRUE subset of (PAPER) options
export const getMediumLeadCard = FeatureFlags.get('digitalInvites')
  ? createSelector(
    getSelectedMedium,
    getDetailSuite,
    getDigitalCard,
    (selectedMedium, detailSuite, digitalCard) => {
      // using switch statement in anticipation of magnet medium
      switch (selectedMedium) {
        case 'DIGITAL':
          return digitalCard;
        case 'PAPER':
        default:
          return detailSuite.leadCard;
      }
    }
  )
  : createSelector(
    getDetailSuite,
    detailSuite => detailSuite.leadCard
  );

export const getVariationOptions = createSelector(
  getDetailSuite,
  detailSuite => detailSuite.variationOptions
);

const getLeadCardVariations = createSelector(
  getMediumLeadCard,
  leadCard => leadCard.variations
);

export const getOptionGroupsByKey = FeatureFlags.get('digitalInvites')
  ? createSelector(
    getSelectedMedium,
    getDetailSuite,
    (selectedMedium, detailSuite) => {
      // using switch statement in anticipation of magnet medium
      switch (selectedMedium) {
        case 'DIGITAL':
          return detailSuite.digitalOptionGroupsByKey;
        case 'PAPER':
        default:
          return detailSuite.leadOptionGroupsByKey;
      }
    }
  )
  : createSelector(
    getDetailSuite,
    detailSuite => detailSuite.leadOptionGroupsByKey
  );

export const getVariationOptionsValues = createSelector(
  getVariationOptions,
  variationOptions => _keys(variationOptions).reduce((result, key) => ({ ...result, [key]: variationOptions[key].value }), {})
);

// INV-905: filter non-digital options when finding digital variations
export const getActiveVariation = createSelector(
  getLeadCardVariations,
  getVariationOptions,
  getSelectedMedium,
  (variations, variationOptions, medium) => {
    const selectedOptions = variationOptions;
    // if postcard, assume 'reverse-printing' is true
    if (selectedOptions['reverse-printing'] && selectedOptions.size === 'postcard') {
      // return the active variation
      return _find(variations, {
        option_values: { ...selectedOptions, 'reverse-printing': 'true' }
      });
    }
    // before finding a match, update the options based on medium
    if (medium === 'DIGITAL') {
      delete selectedOptions['paper-type'];
    }
    // return the active variation
    return _find(variations, {
      option_values: { ...selectedOptions }
    });
  }
);

/* This appears to be unused? */
export const getVisibleActiveVariation = createSelector(
  getLeadCardVariations,
  getVariationOptions,
  getActiveVariation,
  (variations, variationOptions, activeVariation) => {
    let visibleActiveVariation = activeVariation;
    if (variationOptions && variationOptions['reverse-printing']) {
      visibleActiveVariation = _find(variations, {
        option_values: { ...variationOptions, 'reverse-printing': 'false' }
      }) || activeVariation;
    }
    return visibleActiveVariation;
  }
);

export const getActiveVariationImages = createSelector(
  getActiveVariation,
  (activeVariation) => {
    const activeVariationImages = IMAGE_KEYS.reduce((result, imageKey) => {
      if (activeVariation.image_urls[imageKey]) {
        result.push(activeVariation.image_urls[imageKey]);
      }
      return result;
    }, []);
    return activeVariationImages;
  }
);

export const getColorOptions = createSelector(
  getOptionGroupsByKey,
  (optionGroupsByKey) => {
    if (optionGroupsByKey.color && optionGroupsByKey.color.options) {
      return optionGroupsByKey.color.options;
    }
    return null;
  }
);

export const getActiveColorOptionLabel = createSelector(
  getColorOptions,
  getVariationOptions,
  (colorOptions, variationOptions) => {
    const color = _find(colorOptions, { value: variationOptions.color });
    return color.label;
  }
);

export const getMinimumPriceCents = createSelector(
  getLeadCardVariations,
  (variations) => {
    const minPriceVariation = _minBy(variations, variation => variation.price_cents);
    return minPriceVariation.price_cents;
  }
);

/* This appears to be unused? */
export const getActiveVariationPriceCents = createSelector(
  getActiveVariation,
  variation => variation.price_cents
);

/* This appears to be unused? */
export const getVisibleActiveVariationPriceCents = createSelector(
  getVisibleActiveVariation,
  variation => variation.sku.price_cents
);

export const getThemeGroupsForLeadCard = createSelector(
  getLeadCard,
  getThemeGroupsForCards,
  (leadCard, themeGroups) => {
    if (leadCard && themeGroups && themeGroups[leadCard.uuid]) {
      return themeGroups[leadCard.uuid];
    }
    return null;
  }
);

export const getRelatedCardsFromCurrentSuite = createSelector(
  getRelatedCardsFromLeadSuite,
  (relatedCardsFromSuite) => {
    if (relatedCardsFromSuite) {
      return relatedCardsFromSuite.map(relatedCardFromSuite => ({ ...relatedCardFromSuite.lead_card, suiteUUID: relatedCardFromSuite.uuid, suiteName: relatedCardFromSuite.name }));
    }
    return null;
  }
);

export const getCards = state => state.cards.cardCatalog.cards;



// WEBPACK FOOTER //
// ./src/cards/selectors/cardCatalogSelector.js